/**
 * GEDCOM Tree Builder
 *
 * Converts flat token array into hierarchical TreeNode structure.
 * Uses stack-based parsing to track parent-child relationships by level.
 *
 * Part of Phase 4 - Slice 2: Parser Integration
 */

import type { TreeNode } from '../types/TreeNode';

/**
 * Build hierarchical tree from flat token array
 *
 * Algorithm:
 * - Maintain a stack of parent nodes
 * - For each token:
 *   - Pop parents from stack until we find the correct parent (level < token.level)
 *   - If stack is empty, token is a root node
 *   - Otherwise, add token as child of top stack node
 *   - Push token onto stack
 *
 * Example:
 * Input tokens:
 *   { level: 0, tag: 'INDI', pointer: '@I1@' }
 *   { level: 1, tag: 'NAME', value: 'John /Doe/' }
 *   { level: 1, tag: 'SEX', value: 'M' }
 *
 * Output tree:
 *   [
 *     {
 *       level: 0, tag: 'INDI', pointer: '@I1@',
 *       children: [
 *         { level: 1, tag: 'NAME', value: 'John /Doe/', children: [] },
 *         { level: 1, tag: 'SEX', value: 'M', children: [] }
 *       ]
 *     }
 *   ]
 *
 * @param tokens - Flat array of tokens from tokenizer
 * @returns Hierarchical tree structure (root nodes)
 */
export const buildTree = (tokens: TreeNode[]): TreeNode[] => {
  const root: TreeNode[] = [];
  const stack: TreeNode[] = [];

  tokens.forEach((token) => {
    // Pop from stack until we find a parent with lower level
    while (stack.length > 0 && stack[stack.length - 1].level >= token.level) {
      stack.pop();
    }

    // If stack is empty, this is a root-level node
    if (stack.length === 0) {
      root.push(token);
    } else {
      // Otherwise, add as child of current parent
      const parent = stack[stack.length - 1];
      parent.children.push(token);
    }

    // Push current token onto stack (it might be a parent for next tokens)
    stack.push(token);
  });

  return root;
};
